<template>
  <div class="py-1">
    <MenuOption
      v-if="hasUnregisteredSamples && canUser('orders.registerSamples')"
      id="register-all-samples"
      :title="$t('W: Modtag alle prøver')"
      @select="onRegisterAllSamplesClick">
      <template #icon>
        <IconLucidePackageCheck class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="
        tatStartDate !== null &&
        orderStatus !== 'cancelled' &&
        orderStatus !== 'completed' &&
        !isInvoiceLocked &&
        canUser('orders.edit')
      "
      id="update-tat-start-date"
      :title="$t('W: Opdater modtagelsesdato')"
      @select="onUpdateTatStartDateClick">
      <template #icon>
        <IconLucideCalendarClock class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="
        orderStatus !== 'cancelled' &&
        !isInvoiceLocked &&
        canUser('orders.cancel')
      "
      id="cancel"
      :title="$t('W: Annuller ordre')"
      @select="onCancelSelect">
      <template #icon>
        <IconLucideX class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="orderStatus === 'cancelled' && !isInvoiceLocked && canUser('orders.cancel')"
      id="reopen"
      :title="$t('W: Genåbn ordre')"
      @select="onReopenClick">
      <template #icon>
        <IconLucideRotateCcw class="h-4 w-4" />
      </template>
    </MenuOption>
    <!-- Internal / external. Hidden rather than disabled once an invoice exists or the
         order is cancelled, per permission-visibility.md — the backend refuses both, so
         offering the action would only produce an error the user cannot act on. -->
    <!-- An insurance customer's own order can never be made internal (MIL-402) — lab-cloud
         refuses it, so it is only offered the other way, should one ever be internal. -->
    <MenuOption
      v-if="
        orderStatus !== 'cancelled' &&
        !hasInvoice &&
        (isInternal || !isInsuranceCustomerOwnOrder) &&
        canUser('orders.billing')
      "
      id="toggle-internal"
      :title="
        isInternal ? $t('area.order.action.make-external') : $t('area.order.action.make-internal')
      "
      @select="onToggleInternalClick">
      <template #icon>
        <IconLucideBuilding2 class="h-4 w-4" />
      </template>
    </MenuOption>
    <!-- Report language -->
    <MenuOption
      v-if="canUser('orders.edit')"
      id="report-locale"
      :title="$t('W: Rapportsprog')"
      @select="onReportLocaleSelect">
      <template #icon>
        <IconLucideLanguages class="h-4 w-4" />
      </template>
    </MenuOption>
    <div
      v-if="showDevActions"
      class="border-t border-border-subtle mt-1 pt-1">
      <MenuOption
        id="recalculate"
        :title="$t('W: Genberegn statusser')"
        :isDisabled="isRecalculating"
        @select="onRecalculateClick">
        <template #icon>
          <div class="flex items-center gap-1.5">
            <DevBadge />
            <IconLucideRotateCcw
              class="h-4 w-4"
              aria-hidden="true" />
          </div>
        </template>
      </MenuOption>
      <MenuOption
        id="duplicate-order"
        :title="$t('W: Dupliker ordre')"
        :isDisabled="isDuplicating"
        @select="onDuplicateClick">
        <template #icon>
          <div class="flex items-center gap-1.5">
            <DevBadge />
            <IconLucideCopy
              class="h-4 w-4"
              aria-hidden="true" />
          </div>
        </template>
      </MenuOption>
      <MenuOption
        id="recalculate-pricing"
        :title="$t('W: Genberegn priser')"
        :isDisabled="isRecalculatingPricing"
        @select="onRecalculatePricingClick">
        <template #icon>
          <div class="flex items-center gap-1.5">
            <DevBadge />
            <IconLucideRefreshCw
              class="h-4 w-4"
              aria-hidden="true" />
          </div>
        </template>
      </MenuOption>
    </div>
  </div>
</template>

<script lang="ts">
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import type { TOrderStatus } from '@team-milva/lab-models/resources'
import { mapActions, mapState } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'
import { useUserStore } from '@/stores/userStore'

export default {
  name: 'OrderMenu',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    orderStatus: { type: String as () => TOrderStatus, required: true },
    /** A BC draft or a sent invoice holds the order — see getOrderInvoiceLock. */
    isInvoiceLocked: { type: Boolean, required: true },
    hasUnregisteredSamples: { type: Boolean, required: true },
    tatStartDate: { type: Number, default: null },
    reportLocale: { type: String, default: null },
    isInternal: { type: Boolean, required: true },
    /** Draft or sent — either one closes the door on changing the internal state. */
    hasInvoice: { type: Boolean, required: true },
    /** Owned by an insurance customer (MIL-402) — such an order can never be made internal. */
    isInsuranceCustomerOwnOrder: { type: Boolean, default: false },
  },

  data() {
    return {
      isRecalculating: false,
      isRecalculatingPricing: false,
      isDuplicating: false,
    }
  },

  computed: {
    ...mapState(useUserStore, ['showDevActions']),
  },

  methods: {
    ...mapActions(useModalStore, ['open']),
    ...mapActions(useUserStore, ['canUser']),
    ...mapActions(useOrderStore, [
      'recalculateOrderStatuses',
      'recalculateOrderPricing',
      'duplicateOrder',
      'reopenOrder',
      'registerAllSamples',
      'setOrderInternal',
    ]),

    onToggleInternalClick(): void {
      this.$emit('close')
      const next = !this.isInternal
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: next
            ? this.$t('area.order.action.make-internal')
            : this.$t('area.order.action.make-external'),
          textLines: next
            ? [
                this.$t(
                  'area.order.message.make-internal-confirm'
                ),
                this.$t(
                  'area.order.message.make-internal-note'
                ),
              ]
            : [
                this.$t(
                  'area.order.message.make-external-confirm'
                ),
              ],
          onConfirm: async () => {
            try {
              await this.setOrderInternal(this.orderID, next)
              useToast().success(
                next
                  ? this.$t('area.order.message.now-internal')
                  : this.$t('area.order.message.now-external')
              )
            } catch {
              useToast().error(this.$t('area.order.message.change-type-failed'))
            }
          },
        },
      })
    },

    onReportLocaleSelect(): void {
      this.$emit('close')
      this.open({
        name: 'UpdateOrderReportLocaleModal',
        width: 'sm',
        props: {
          orderID: this.orderID,
          reportLocale: this.reportLocale,
        },
      })
    },

    onRegisterAllSamplesClick(): void {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Modtag alle prøver'),
          textLines: [
            this.$t(
              'W: Er du sikker på, at du vil markere alle ikke-modtagne prøver på denne ordre som modtaget?'
            ),
          ],
          onConfirm: async () => {
            try {
              await this.registerAllSamples(this.orderID)
              useToast().success(this.$t('W: Alle prøver modtaget'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke modtage prøver'))
            }
          },
        },
      })
    },

    onCancelSelect(): void {
      this.$emit('close')
      this.open({
        name: 'CancelOrderModal',
        width: 'md',
        props: { orderID: this.orderID },
      })
    },

    async onRecalculateClick(): Promise<void> {
      this.isRecalculating = true
      try {
        await this.recalculateOrderStatuses(this.orderID)
        useToast().success(this.$t('W: Statusser genberegnet'))
      } catch {
        useToast().error(this.$t('W: Kunne ikke genberegne statusser'))
      } finally {
        this.isRecalculating = false
        this.$emit('close')
      }
    },

    /**
     * DEV: a copy with its own order number, ready to invoice again. The order number is the BC "Your
     * Reference", so a copy is how an invoice flow is repeated against BC without the posted invoice of
     * the first run blocking it. Opens the copy on its Faktura tab.
     */
    async onDuplicateClick(): Promise<void> {
      this.isDuplicating = true
      try {
        const copy = await this.duplicateOrder(this.orderID)
        useToast().success(this.$t('W: Ordre duplikeret som #{orderNumber}', { orderNumber: copy.orderNumber }))
        this.$emit('close')
        void this.$router.push({ name: 'OrderInvoice', params: { orderID: copy.orderID } })
      } catch {
        useToast().error(this.$t('W: Kunne ikke duplikere ordren'))
        this.$emit('close')
      } finally {
        this.isDuplicating = false
      }
    },

    async onRecalculatePricingClick(): Promise<void> {
      this.isRecalculatingPricing = true
      try {
        await this.recalculateOrderPricing(this.orderID)
        useToast().success(this.$t('W: Priser genberegnet'))
      } catch {
        useToast().error(this.$t('W: Kunne ikke genberegne priser'))
      } finally {
        this.isRecalculatingPricing = false
        this.$emit('close')
      }
    },

    async onReopenClick(): Promise<void> {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Genåbn ordre'),
          textLines: [
            this.$t('W: Er du sikker på, at du vil genåbne denne ordre?'),
          ],
          onConfirm: async () => {
            try {
              await this.reopenOrder(this.orderID)
              useToast().success(this.$t('W: Ordre genåbnet'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke genåbne ordre'))
            }
          },
        },
      })
    },

    onUpdateTatStartDateClick(): void {
      this.$emit('close')
      this.open({
        name: 'UpdateOrderTatStartDateModal',
        width: 'sm',
        props: {
          orderID: this.orderID,
          currentTatStartDate: this.tatStartDate,
        },
      })
    },
  },
}
</script>
